import { useState } from 'react';
import { Card, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';

/**
 * A chart in a card, with the table view built in.
 *
 * The table is not a nice-to-have. Three of the light-mode series colours sit
 * below 3:1 against the surface, and the palette rule for that is explicit:
 * ship visible direct labels or a table view. Every chart in this app gets the
 * table, which also makes the numbers copyable and gives screen readers a real
 * alternative to an SVG.
 */
export function ChartCard({
  title,
  subtitle,
  icon,
  actions,
  table,               // { columns: [{key,header,align}], rows: [...] }
  children,
  className = '',
}) {
  const [showTable, setShowTable] = useState(false);

  return (
    <Card className={className}>
      <CardHeader
        title={title}
        subtitle={subtitle}
        icon={icon}
        actions={
          <>
            {actions}
            {table && (
              <Button
                variant="ghost"
                size="sm"
                icon={showTable ? 'chart' : 'table'}
                onClick={() => setShowTable((v) => !v)}
                aria-pressed={showTable}
              >
                {showTable ? 'Chart' : 'Table'}
              </Button>
            )}
          </>
        }
      />
      <div className="p-4">
        {showTable && table ? (
          <div className="max-h-72 overflow-auto rounded-lg border border-line">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-2">
                <tr>
                  {table.columns.map((column) => (
                    <th
                      key={column.key}
                      className={`border-b border-line px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-3 ${
                        column.align === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, index) => (
                  <tr key={row.key ?? index} className="border-b border-line/60 last:border-0">
                    {table.columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-3 py-1.5 text-[13px] text-ink ${
                          column.align === 'right' ? 'text-right tabular-nums' : ''
                        }`}
                      >
                        {row[column.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </div>
    </Card>
  );
}

/** The date-range control every history view shares. */
export const RANGES = [
  { value: '1h', label: '1H', ms: 3600e3 },
  { value: '6h', label: '6H', ms: 6 * 3600e3 },
  { value: '24h', label: '24H', ms: 86400e3 },
  { value: '7d', label: '7D', ms: 7 * 86400e3 },
  { value: '30d', label: '30D', ms: 30 * 86400e3 },
];

export function rangeToWindow(value) {
  const range = RANGES.find((r) => r.value === value) || RANGES[2];
  const to = new Date();
  return { from: new Date(to.getTime() - range.ms), to, label: range.label };
}
